import { useCallback, useEffect, useMemo, useState } from 'react';
import { Combobox, Field, Option, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { editWorkItemStyles } from '../styles';
import { useAutomationRuleActions } from '../../use-actions';
import { ActionItemType, EditWorkItemActionType } from '../../types';
import { useAzureProvider } from 'src/azure/context';
import ClearebleButtonInput from 'src/components/shared/cleareble-button-input';
import { useSelectInput } from './use-select-input';

interface Props {
  id?: string;
  actionId: string;
  validationError: string | null;
	isMultiSelect?: boolean;
}

export default function SelectInput(props: Props) {
	const classes = editWorkItemStyles();
	const {applicationContext} = useAzureProvider();

	const selectInputHook = useSelectInput();
	const {actions, setActions, resetValidationErrors} = useAutomationRuleActions();

	const [onInitialLoading, setOnInitialLoading] = useState(false);
	const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
	const [status, setStatus] = useState<'searching' | 'selected'>('selected');
	const [text, setText] = useState<string>('');

	const current: EditWorkItemActionType = useMemo(() => {
		const foundAction = actions.find(item => item.id === props.actionId) as ActionItemType;
		return foundAction!.editWorkItem as EditWorkItemActionType;
	}, [actions, props.actionId]);

	const onChangeText = useCallback((value: string) => {
		setStatus('searching');
		setText(value);
		
		if (value === '') {
			getOptionsAsync();
		}
		
		if (value !== '') {
			const filteredOptions = options.filter(item => item.label.toLowerCase().includes(value.toLowerCase()));
			setOptions(filteredOptions);
		}
	}, [options]);

	const setOptionSelect = useCallback((value: string) => {
		if (value) {
			resetValidationErrors();
			const foundAction = actions.find(item => item.id === props.actionId);
			setStatus('selected');
	
			if (foundAction) {
				if (props.isMultiSelect) {
					setActions(oldActions => {
						const current = oldActions.find(item => item.id === props.actionId) as ActionItemType;
						const others = oldActions.filter(item => item.id !== props.actionId);

						const selectedValues = current.editWorkItem!.selectValue.includes(value) ?
							current.editWorkItem!.selectValue.filter(item => item !== value) :
							[...current.editWorkItem!.selectValue, value];

						return [...others, {
							...current, editWorkItem: {
								...current.editWorkItem as EditWorkItemActionType,
								selectValue: selectedValues
							}
						}];
					});
				}

				if (!props.isMultiSelect) {
					setActions(oldActions => {
						const current = oldActions.find(item => item.id === props.actionId) as ActionItemType;
						const others = oldActions.filter(item => item.id !== props.actionId);
		
						return [...others, {
							...current, editWorkItem: {
								...current.editWorkItem as EditWorkItemActionType,
								selectValue: [value],
							}
						}];
					});
				}
			}
		}
	}, [props.isMultiSelect]);
  
	const valueRender = useMemo(() => {
		if (current && current.selectValue && status === 'selected') {
			return options.filter((item) => current.selectValue.includes(item.value)).map((item) => item.label).join(', ');
		}

		if (text) {
			return text;
		}

		return '';
	}, [text, current, options, status]);

	const selectedOptions = useMemo(() => {
		const filteredValues = options.filter(item => current.selectValue.includes(item.value));

		if (current.selectValue && filteredValues.length > 0) {
			return filteredValues.map(item => item.value);
		}

		return [];
	}, [current, options]);

	const getOptionsAsync = useCallback(async () => {
		const resultOptions = await selectInputHook.loadOptions({
			organizationName: applicationContext.organizationName,
			projectId: applicationContext.projectId,
			fieldName: current.workItemField,
			userId: applicationContext.userId,
		});

		setOptions(resultOptions);
	}, [current.workItemField]);

	const clearOptionSelect = useCallback(() => {
		const foundAction = actions.find(item => item.id === props.actionId);
		setStatus('selected');

		if (foundAction) {
			setActions(oldActions => {
				const current = oldActions.find(item => item.id === props.actionId) as ActionItemType;
				const others = oldActions.filter(item => item.id !== props.actionId);

				return [...others, {
					...current, editWorkItem: {
						...current.editWorkItem as EditWorkItemActionType,
						selectValue: []
					}
				}];
			});
		}
	}, [actions, props.actionId]);

	useEffect(() => {
		setOnInitialLoading(true);
		getOptionsAsync()
			.finally(() => {
				setOnInitialLoading(false);
			});
	}, []);
  
	if (onInitialLoading) {
		return (
			<Skeleton className={classes.skeletonRoot}>
				<SkeletonItem className={classes.skeletonItem} />
			</Skeleton>
		);
	}

	return (
		<Field
			className={classes.dropdownRoot}
			validationMessage={props.validationError}
		>
			<Combobox
				clearable
				inputMode='search'
				autoComplete='off'
				multiselect={props.isMultiSelect}
				className={classes.dropdown}
				appearance='outline'
				placeholder='Choose a value'
				positioning="below-start"
				value={valueRender}
				selectedOptions={selectedOptions}
				clearIcon={<ClearebleButtonInput onClick={clearOptionSelect} />}
				onOptionSelect={(_, data) => {
					setOptionSelect(data.optionValue as string);
				}}
				onChange={(ev) => {
					onChangeText(ev.target.value);
				}}
				onBlur={() => {
					setStatus('selected');
					getOptionsAsync();
				}}
			>
				<div style={{maxHeight: 240}}>
					{options.map((item, idx) => (
						<Option key={idx} value={item.value}>{item.label}</Option>
					))}
					{options.length === 0 && (
						<div className={classes.noOptions}>
							<span>No options</span>
						</div>
					)}
				</div>
			</Combobox>
		</Field>
	);
}
